import React, { useEffect, useState } from 'react';
import { Avatar, Collapse } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { DisplayValue } from '../devices/shared-ui';
import DeviceLoadingSkeleton from '../devices/shared-ui/deviceLoadingSkeleton';
import {
  DeviceInfo,
  DeviceInformation,
} from '../devices/shared-ui/device-info';
import {
  DeviceAlerts,
  ToggleAlertDetailsButton,
} from '../devices/shared-ui/alerts-info';
import { updateCacheFromSubscriptionEvent } from '../../../helpers/subscriptionUtils';
import moment from 'moment';
import {
  VirtualPowerPlant,
  useVirtualPowerPlantDetailQuery,
  useVirtualPowerPlantDetailUpdatesSubscription,
} from '../../../types/generated-types';
import { EnergyInverterComponent } from '../devices/virtual-power-plant/energy-inverter';
import { EnergySwitchPanelComponent } from '../devices/virtual-power-plant/energy-switch-panel';

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const VppIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 2L2 7v10l10 5 10-5V7L12 2z" fill="currentColor" />
    <path
      d="M12 4L4 8v8l8 4 8-4V8l-8-4z"
      fill="currentColor"
      fillOpacity="0.5"
    />
  </svg>
);

interface VirtualPowerPlantProps {
  id: string;
}

export function VirtualPowerPlantComponent({ id }: VirtualPowerPlantProps) {
  const [participant, setParticipant] = useState<Partial<VirtualPowerPlant>>();
  const [expanded, setExpanded] = useState(false);
  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const [participantInfo, setParticipantInfo] = useState<DeviceInformation[]>(
    [],
  );

  const { data } = useVirtualPowerPlantDetailQuery({
    variables: { id },
  });

  useVirtualPowerPlantDetailUpdatesSubscription({
    variables: { id },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (data?.virtualPowerPlantById) {
      setParticipant(data.virtualPowerPlantById as Partial<VirtualPowerPlant>);
    }
  }, [data]);

  useEffect(() => {
    if (participant) {
      const info: DeviceInformation[] = [
        {
          'Participant Identification': {
            'Participant Name':
              participant.name ?? 'Unnamed Virtual Power Plant',
            'Participant ID': participant._id ?? 'Unidentified',
            Location: participant.location ?? 'Unknown',
            'Last Updated': moment(participant.timestamp).fromNow(),
          },
          'System Status': {
            'Operational Status':
              participant.operationalStatus?.value ?? 'Unknown',
            'Update Status': `${participant.updateStatus ?? 'Unknown'}`,
          },
          'System Integration': {
            'Property ID': participant.propertyId ?? 'Not assigned',
            'Unit ID': participant.unitId ?? 'Not assigned',
            'Inverter ID': participant.inverterId ?? 'Not assigned',
            'Switch Panel ID': participant.switchPanelId ?? 'Not assigned',
          },
        },
      ];
      setParticipantInfo(info);
    }
  }, [participant]);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  if (!participant) {
    return <DeviceLoadingSkeleton size="large" />;
  }

  return (
    <Card
      sx={{
        boxShadow: 1,
        p: 0,
        width: '100%',
        borderRadius: '4px',
        marginTop: '4px',
      }}
    >
      <CardHeader
        sx={{
          paddingTop: '0px',
          paddingLeft: '10px',
          paddingRight: '10px',
          paddingBottom: '0px',
          border: '1px solid rgba(0,0,0, 0.1)',
        }}
        title={
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <div style={{ flex: '0 0 auto', flexDirection: 'column' }}>
              <Box sx={{ padding: '0px' }}>
                <div
                  style={{
                    display: 'inline-flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingTop: '6px',
                  }}
                >
                  <Typography
                    variant="h5"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      paddingTop: '3px',
                    }}
                  >
                    {participant?.name}
                  </Typography>
                </div>
              </Box>
            </div>
            <div style={{ flex: '1 1 auto', flexDirection: 'column' }} />
            <div style={{ flex: '0 0 auto', flexDirection: 'column' }}>
              <Box sx={{ p: 0 }}>
                <Stack
                  direction={'row'}
                  justifyContent={'space-between'}
                  divider={<Divider orientation="vertical" flexItem />}
                >
                  <div style={{ marginLeft: '6px' }}>&nbsp;</div>
                  <DisplayValue
                    value={participant.operationalStatus?.value}
                    units=""
                    size={'h3'}
                    unitsSize={'14px'}
                    style={{
                      paddingTop: '8px',
                      paddingLeft: '10px',
                      paddingRight: '10px',
                    }}
                  />
                  {participant.inverterId ? (
                    <EnergyInverterComponent id={participant.inverterId} />
                  ) : null}
                  {participant.switchPanelId ? (
                    <EnergySwitchPanelComponent
                      id={participant.switchPanelId}
                    />
                  ) : null}
                  <div style={{ marginRight: '6px' }}>&nbsp;</div>
                </Stack>
              </Box>
            </div>
          </div>
        }
        avatar={
          <Avatar
            sx={{
              backgroundColor: 'transparent',
              padding: '0px',
              margin: '0px',
            }}
          >
            <VppIcon />
          </Avatar>
        }
        action={
          <Stack direction="row" spacing={1}>
            <ToggleAlertDetailsButton
              expand={alertsExpanded}
              onClick={() => setAlertsExpanded(!alertsExpanded)}
              device={participant}
              buttonLabel="Alerts"
            />
            <ExpandMore
              expand={expanded}
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="show more"
            >
              <ExpandMoreIcon />
            </ExpandMore>
          </Stack>
        }
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 2 }}>
          <DeviceInfo infoEntries={participantInfo} />
        </Box>
      </Collapse>
      <Collapse in={alertsExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 2 }}>
          <DeviceAlerts device={participant} alertsExpanded={alertsExpanded} />
        </Box>
      </Collapse>
    </Card>
  );
}
